import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getTypeIdFromPath, nodeTypes } from './NodeClassifier';
import esTranslations from './locales/es/translation.json';
import enTranslations from './locales/en/translation.json';

const FALLBACK_LANGUAGE = 'es';
const SUPPORTED_LANGUAGES = new Set(['es', 'en']);
const LANGUAGE_COOKIE_NAME = 'lang';

const getBaseLanguage = (language?: string | null) => {
    const normalizedLanguage = language?.trim().toLowerCase();
    if (!normalizedLanguage) {
        return null;
    }

    const [baseLanguage] = normalizedLanguage.split('-');
    return baseLanguage || null;
};

const toSupportedLanguage = (language?: string | null) => {
    const baseLanguage = getBaseLanguage(language);
    if (!baseLanguage) {
        return null;
    }

    return SUPPORTED_LANGUAGES.has(baseLanguage) ? baseLanguage : null;
};

const getBrowserLanguage = () => {
    if (typeof navigator === 'undefined') {
        return null;
    }

    const preferredLanguage = navigator.languages?.[0] || navigator.language;
    return toSupportedLanguage(preferredLanguage);
};

const getLanguageFromCookie = () => {
    if (typeof document === 'undefined') {
        return null;
    }

    const languageCookie = document.cookie?.split('; ').find((cookie) => cookie.startsWith(`${LANGUAGE_COOKIE_NAME}=`));

    if (!languageCookie) {
        return null;
    }

    const [, cookieLanguage] = languageCookie.split('=');
    return toSupportedLanguage(cookieLanguage);
};

export const resolveAppLanguage = (language?: string | null) => {
    return toSupportedLanguage(language) || getLanguageFromCookie() || getBrowserLanguage() || FALLBACK_LANGUAGE;
};

i18n.use(initReactI18next).init({
    resources: {
        es: {
            translation: esTranslations,
        },
        en: {
            translation: enTranslations,
        },
    },
    lng: resolveAppLanguage(),
    fallbackLng: FALLBACK_LANGUAGE,
    // Keys are natural-language strings (the translation files are flat), so a `:` or `.`
    // in a key (e.g. "From:", "To:", "Location:", "Add annotation: {{type}}") must NOT be
    // treated as a namespace/nested-key separator — otherwise those lookups resolve to empty.
    nsSeparator: false,
    keySeparator: false,
    // React escapes text nodes on its own, so i18next's HTML escaping only mangles interpolated
    // values — a formatted date came out as "6&#x2F;8&#x2F;26". No translated string is fed to
    // dangerouslySetInnerHTML, so turning it off is safe.
    interpolation: { escapeValue: false },
});

/**
 * The language the plant that `node` belongs to reads in.
 *
 * For text that gets STORED rather than displayed — a sensor name, which lives in the database
 * from then on — the plant's language is the right one, not the language of whoever happens to be
 * logged in. Displayed text keeps using the active language.
 *
 * The plant carries it in the tree (the backend resolves country -> language, see
 * `COUNTRIES_LANGUAGES`). Falls back to the active language when the tree has no plant language,
 * which is what naming did before this existed.
 */
export const getPlantLanguage = (node: any, treeMap: any) => {
    // `path` leads up to the node without including it, so a plant only matches by its own type.
    const plantId =
        node?.type === nodeTypes.plant ? String(node.id) : getTypeIdFromPath(node, nodeTypes.plant, treeMap);

    return toSupportedLanguage(treeMap?.[plantId]?.static_data?.language) || i18n.language;
};

export const updateLanguage = (language?: string | null) => {
    const currentLanguage = toSupportedLanguage(i18n.language) || FALLBACK_LANGUAGE;
    const nextLanguage = resolveAppLanguage(language);

    if (nextLanguage !== currentLanguage) {
        i18n.changeLanguage(nextLanguage);
    }
};

export const updateTranslations = (translations: any) => {
    const translationKey = 'translation';
    for (const language in translations) {
        const newTranslations = translations[language][translationKey];
        if (newTranslations) {
            i18n.addResourceBundle(language.substring(0, 2), translationKey, newTranslations, true, true);
        }
    }
};

export default i18n;
